import React, { useEffect, useRef } from 'react';

export interface ContextMenuItem {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    isDanger?: boolean;
}

interface ContextMenuProps {
    x: number;
    y: number;
    items: ContextMenuItem[];
    onClose: () => void;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, items, onClose }) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        // Handle escape key
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    // Adjust position to keep in viewport
    const style: React.CSSProperties = {
        top: y,
        left: x,
    };

    return (
        <div
            ref={menuRef}
            className="fixed z-[100] min-w-[180px] bg-obsidian-900 rounded-xl border border-gold-500/20 shadow-2xl shadow-black/50 overflow-hidden py-1 animate-in fade-in zoom-in-95 duration-100"
            style={style}
        >
            {items.map((item, index) => (
                <button
                    key={index}
                    onClick={() => {
                        item.onClick();
                        onClose();
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${item.isDanger
                            ? 'text-red-400 hover:bg-red-500/10 hover:text-red-300'
                            : 'text-gray-300 hover:bg-gold-500/10 hover:text-gold-400'
                        }`}
                >
                    {item.icon && <span className="w-4 h-4 opacity-70">{item.icon}</span>}
                    <span className="font-medium">{item.label}</span>
                </button>
            ))}
        </div>
    );
};
